import type { ReactElement } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import type { Locale } from '@skytech/types'
import { DEFAULT_LOCALE } from '@skytech/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useIntl } from 'react-intl'

import { useAppContext } from '@/app/index.context'
import { commonQueries } from '@/resources/queries'
import { commonTranslationResources } from '@/resources/translations'

const MAX_ID_LENGTH = 50

const cropString = (string: string) => string.slice(0, MAX_ID_LENGTH).toLowerCase()

// Prepares translation
export const useMessage = (id = '') => {
  const { data, isSuccess, refetch } = useQuery(
    commonQueries.translationList(),
    commonTranslationResources.getList,
    { staleTime: Infinity }
  )

  const { locale } = useAppContext()
  const croppedId = cropString(id)

  const { mutate } = useMutation(
    // Set description current url as description
    () =>
      commonTranslationResources.post({
        locale,
        code: croppedId,
        text: id,
        description: window.location.pathname,
      }),
    {
      onSettled: refetch,
    }
  )

  useEffect(() => {
    const messages = data ? data[locale] || data[DEFAULT_LOCALE] : {}
    if (!messages[croppedId] && isSuccess && locale) {
      mutate()
    }
  }, [croppedId, data, isSuccess, locale, mutate])

  return croppedId
}

// Returns translation
export const useText = (id: string, values: Record<string, string> = {}) => {
  const croppedId = useMessage(id)
  const { formatMessage } = useIntl()

  return formatMessage({ id: croppedId }, values)
}

// Returns prepare-translation function
const usePrepareTranslation = () => {
  const createdTranslationIds = useRef<string[] | null>(null)
  const locale = useIntl().locale as Locale

  const { data, isSuccess, refetch } = useQuery(
    commonQueries.translationList(),
    commonTranslationResources.getList,
    { staleTime: Infinity }
  )
  const { mutate } = useMutation(commonTranslationResources.post, {
    onSettled: refetch,
  })

  return useCallback(
    (id: string) => {
      const croppedId = cropString(id)

      if (!data) return croppedId

      const doesTranslationExist = locale && croppedId && data[locale]?.[croppedId]
      const hasTranslationBeenCreated = createdTranslationIds.current?.includes(croppedId)

      if (isSuccess && !doesTranslationExist && !hasTranslationBeenCreated) {
        createdTranslationIds.current = [...(createdTranslationIds.current || []), croppedId]
        mutate({
          locale,
          code: croppedId,
          text: id,
        })
      }

      return croppedId
    },
    [data, isSuccess, locale, mutate]
  )
}

// Returns translate function
export const useTranslation = () => {
  const { formatMessage } = useIntl()
  const prepareTranslation = usePrepareTranslation()

  return useCallback(
    (id: string, values: Record<string, string> = {}) =>
      formatMessage({ id: prepareTranslation(id) }, values),
    [formatMessage, prepareTranslation]
  )
}

// Returns translate function with JSX support
export const useTranslationWithJSXSupport = () => {
  const { formatMessage } = useIntl()
  const prepareTranslation = usePrepareTranslation()

  return useCallback(
    (id: string, values: Record<string, string | ReactElement> = {}) =>
      formatMessage({ id: prepareTranslation(id) }, values),
    [formatMessage, prepareTranslation]
  )
}
